import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { Box, Center, Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Tag, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { DirectEmissions } from "./components/DirectEmissions";
import { CategoryQuestionnaire } from "./components/Categories";
import { IndirectEmissions } from "./components/IndirectEmissions";
import { useAuth } from "../../context";
import {
  directEmissionsKeys,
  indirectEmissionsKeys,
  useDownloadScopeOneTemplate,
  useDownloadScopeTwoTemplate,
  useGetOrganizationStructure,
  useGetQuestionnaireById,
  useGetQuestionnaireDataById,
  useImportScopeOneAndTwoData
} from "../../hooks/query";
import { isScopeThreeVisible, QUESTIONNAIRE_STATUSES, showErrorToast, showSuccessToast } from "../../utils";
import { Card } from "../../components/Card";
import Page from "../../components/Page/Page";
import { ScopePanel } from "../../components";
import { ScopeHeaderRow } from "../../components/ScopeHeaderRow";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../context/Store/slices/appSpinnerSlice";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { ReturnToList } from "../SupplierQuestionnaire/components/ReturnToList";
import { routes } from "../../config";
import { AssigneeHeader } from "../../components/AssigneeHeader";
import { getTabIndexes, TAB_TYPES } from "./helpers";

function OrganizationQuestionnaire({ questionnaireId, isOpenedInModal }) {
  const headingRef = useRef(null);
  const { t: globalTranslation } = useTranslation("translation");
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationQuestionnaire"
  });
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [tabIndex, setTabIndex] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => headingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), [tabIndex]);
  const assignee = {
    name: user.name,
    avatarUrl: user.picture
  };
  // These tab indexes are introduced due to the variable set of sections of each individual questionnaire
  const [indexes, setIndexes] = useState({});

  const [questionnaireData, setQuestionnaireData] = useState();
  const [submitAndReturnToList, setSubmitAndReturnToList] = useState(false);

  const {
    data: organizationStructure
  } = useGetOrganizationStructure();

  const getDataConfig = {
    onSuccess: response => {
      setQuestionnaireData(response);
      setIndexes(getTabIndexes(response));
    },
    onError: err => err.response?.status !== 404 ? showErrorToast(toast, err.message) : null,
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0,
    refetchOnWindowFocus: false
  };

  const {
    data: userRelatedData,
    isLoading: isUserRelatedDataLoading,
    isFetching: isUserRelatedDataFetching,
    error: userRelatedError
  } = useGetQuestionnaireDataById(questionnaireId ?? null, {
    ...getDataConfig,
    enabled: questionnaireId !== null && !isNaN(questionnaireId) && !isOpenedInModal
  });

  const {
    data: planningData,
    isLoading: isPlanningDataLoading,
    isFetching: isPlanningDataFetching,
    error: planningError
  } = useGetQuestionnaireById(questionnaireId ?? null, {
    ...getDataConfig,
    enabled: questionnaireId !== null && !isNaN(questionnaireId) && isOpenedInModal
  });

  const importDataConfig = {
    onSuccess: () => {
      showSuccessToast(toast, t("page.imported"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
      queryClient.invalidateQueries(indirectEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ??
      err.response?.data?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: importData } = useImportScopeOneAndTwoData(importDataConfig);

  const handleDataImport = (e, importBtnId) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      dispatch(showAppSpinner());
      document.getElementById(importBtnId).value = null;
      importData({
        questionnaireId: questionnaireData?.id,
        data: formData
      });
    }
  };

  const executeNavigation = () => {
    if (assignee?.name?.toLowerCase() === questionnaireData?.assigneeName?.toLowerCase()) {
      // The assignee of the questionnaire is editing it, so we are returning to 'Organisation Questionnaires' page
      navigate(routes.dataCollectionOrganisationQuestionnaires);
    } else {
      // Admin is editing someone else's questionnaire, so we are returning to 'Data entry planning' page
      navigate(routes.dataCollectionDataEntryPlanning);
    }
  };

  const handleReturnToListClick = () => {
    if (questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED || tabIndex === indexes[TAB_TYPES.DIRECT_EMISSIONS] ||
      tabIndex === indexes[TAB_TYPES.INDIRECT_EMISSIONS]) {
      executeNavigation();
    } else {
      setSubmitAndReturnToList(true);
    }
  };

  const handleNavigationToList = () => {
    setSubmitAndReturnToList(false);
    executeNavigation();
  };

  const downloadTemplateConfig = {
    onMutate: () => dispatch(showAppSpinner()),
    onError: err => {
      const parsedErrorObj = JSON.parse(String.fromCharCode.apply(null, new Int8Array(err.response.data)));
      showErrorToast(toast, parsedErrorObj?.message ?? err.message);
    },
    onSettled: () => dispatch(hideAppSpinner()),
    cacheTime: 0
  };

  const { mutate: downloadScopeOneTemplate } = useDownloadScopeOneTemplate(downloadTemplateConfig);
  const { mutate: downloadScopeTwoTemplate } = useDownloadScopeTwoTemplate(downloadTemplateConfig);

  const renderTabs = () => {
    return <>
      <AssigneeHeader
        assignee={assignee && questionnaireData &&
        assignee?.name?.toLowerCase() !== questionnaireData?.assigneeName?.toLowerCase() ?
          { name: questionnaireData.assigneeName } : assignee
        }
        period={questionnaireData?.period}
        customComponent={!isOpenedInModal ?
          <ReturnToList
            onClickHandler={handleReturnToListClick}
            title={questionnaireData?.status !== QUESTIONNAIRE_STATUSES.FINALIZED ?
              globalTranslation("returnToListTooltip") : null
            }
          /> : null
        }
      />
      <Tabs colorScheme="customScheme" index={tabIndex} onChange={setTabIndex}>
        <Center>
          <TabList borderBottom="none">
            {questionnaireData.configurations.directEmissionsShouldBeFilled &&
              <Tab
                _focus={{ boxShadow: "none" }}
                isDisabled={
                  questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED || isOpenedInModal ?
                    false : tabIndex !== indexes[TAB_TYPES.DIRECT_EMISSIONS]
                }
              >
                {t("tabs.scope1")}
              </Tab>
            }
            {questionnaireData.configurations.indirectEmissionsShouldBeFilled &&
              <Tab
                _focus={{ boxShadow: "none" }}
                marginInline={8}
                isDisabled={
                  questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED || isOpenedInModal ?
                    false : tabIndex !== indexes[TAB_TYPES.INDIRECT_EMISSIONS]
                }
              >
                {t("tabs.scope2")}
              </Tab>
            }
            {isScopeThreeVisible(questionnaireData) &&
              <Tab
                _focus={{ boxShadow: "none" }}
                isDisabled={
                  questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED || isOpenedInModal ?
                    false : tabIndex !== indexes[TAB_TYPES.CATEGORIES]
                }
              >
                {t("tabs.scope3")}
              </Tab>
            }
          </TabList>
        </Center>
        <TabPanels>
          {
            questionnaireData.configurations.directEmissionsShouldBeFilled &&
            <TabPanel padding="0">
              <ScopePanel
                customHeader={<ScopeHeaderRow
                  title={t("scope1.info.title")}
                  description={t("scope1.info.subtitle")}
                  importBtnId={"importScopeOneDataBtn"}
                  handleDataImport={handleDataImport}
                  isPreview={isOpenedInModal ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                  handleTemplateDownload={() => {
                    downloadScopeOneTemplate();
                  }}
                />}
              >
                {organizationStructure && <DirectEmissions
                  organizationStructure={organizationStructure}
                  defaultValues={questionnaireData}
                  handleTabChange={setTabIndex}
                  hasNextScope={
                    questionnaireData.configurations.indirectEmissionsShouldBeFilled
                    || isScopeThreeVisible(questionnaireData)
                  }
                  isPreview={isOpenedInModal ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                />}
              </ScopePanel>
            </TabPanel>
          }
          {questionnaireData.configurations.indirectEmissionsShouldBeFilled &&
            <TabPanel padding="0">
              <ScopePanel
                customHeader={<ScopeHeaderRow
                  title={t("scope2.info.title")}
                  description={t("scope2.info.subtitle")}
                  importBtnId={"importScopeTwoDataBtn"}
                  handleDataImport={handleDataImport}
                  isPreview={isOpenedInModal ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                  handleTemplateDownload={() => {
                    downloadScopeTwoTemplate();
                  }}
                />}
              >
                {organizationStructure && <IndirectEmissions
                  organizationStructure={organizationStructure}
                  defaultValues={questionnaireData}
                  handleTabChange={setTabIndex}
                  hasPrevScope={questionnaireData.configurations.directEmissionsShouldBeFilled}
                  hasNextScope={isScopeThreeVisible(questionnaireData)}
                  isPreview={isOpenedInModal ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                />}
              </ScopePanel>
            </TabPanel>
          }
          {isScopeThreeVisible(questionnaireData) &&
            <TabPanel padding="0">
              <ScopePanel
                title={t("scope3.info.title")}
                description={t("scope3.info.subtitle")}
              >
                <CategoryQuestionnaire
                  defaultValues={questionnaireData}
                  handleTabChange={setTabIndex}
                  hasPrevScope={
                    questionnaireData.configurations.indirectEmissionsShouldBeFilled
                    || questionnaireData.configurations.directEmissionsShouldBeFilled
                  }
                  returnToListRequested={submitAndReturnToList}
                  declineReturnToListRequest={() => setSubmitAndReturnToList(false)}
                  returnToList={handleNavigationToList}
                  allowedToSubmit={isScopeThreeVisible(questionnaireData) && tabIndex === indexes[TAB_TYPES.CATEGORIES]}
                  isPreview={isOpenedInModal ? true : questionnaireData.status === QUESTIONNAIRE_STATUSES.FINALIZED}
                />
              </ScopePanel>
            </TabPanel>
          }
        </TabPanels>
      </Tabs>
    </>;
  };

  if (isOpenedInModal &&
    (planningError || (!isPlanningDataLoading && !isPlanningDataFetching && typeof planningData === "undefined"))) {
    return <Flex blockSize="full">
      <Center flex="1">
        <Box textAlign="center">
          <Tag size="lg">{globalTranslation("somethingWentWrong")}</Tag>
        </Box>
      </Center>
    </Flex>;
  } else if (!isOpenedInModal && (
    (userRelatedError && userRelatedError.response.status === 404) ||
    (!isUserRelatedDataLoading && !isUserRelatedDataFetching && typeof userRelatedData === "undefined")
  )) {
    return <Flex blockSize="full">
      <Center flex="1">
        <Box textAlign="center">
          {userRelatedError && userRelatedError.response.status === 404 ?
            <>
              <Tag size="lg">{globalTranslation("noAvailableQuestionnaire")}</Tag>
              <Box>
                <ReturnToList onClickHandler={() => navigate(routes.dataCollectionOrganisationQuestionnaires)} />
              </Box>
            </> :
            <Tag size="lg">{globalTranslation("somethingWentWrong")}</Tag>
          }
        </Box>
      </Center>
    </Flex>;
  } else {
    return questionnaireData && typeof questionnaireData !== "undefined" ?
      !isOpenedInModal ?
        <Page title={t("page.title")} subTitle={questionnaireData.description}>
          <Card>
            {renderTabs()}
          </Card>
        </Page> :
        renderTabs() : null;
  }
}

export default OrganizationQuestionnaire;
